import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import ProfilePanel from './ProfilePanel'
import { TIMELINE_FILTERS, LIST_PREVIEW_LIMIT } from './profileDetailSections'
import { timelineFilterCounts } from './buildComplianceTimeline'

const CATEGORY_CLASS = {
  kyc: 'bg-red-50 text-red-700',
  screening: 'bg-amber-50 text-amber-800',
  investigations: 'bg-pink-50 text-pink-700',
  uar: 'bg-slate-100 text-slate-600',
  alerts: 'bg-blue-50 text-blue-700',
}

const CATEGORY_LABEL = {
  kyc: 'KYC',
  screening: 'Screening',
  investigations: 'Investigation',
  uar: 'UAR',
  alerts: 'Alert',
}

const PREVIEW_LIMIT = LIST_PREVIEW_LIMIT

export default function ProfileComplianceTimeline({ events, onViewAmlCheck }) {
  const [filter, setFilter] = useState('all')
  const [expanded, setExpanded] = useState(false)
  const counts = useMemo(() => timelineFilterCounts(events), [events])

  const filtered = useMemo(() => {
    if (filter === 'all') return events
    return events.filter((e) => e.category === filter)
  }, [events, filter])

  const visible = expanded ? filtered : filtered.slice(0, PREVIEW_LIMIT)
  const hiddenCount = Math.max(0, filtered.length - PREVIEW_LIMIT)

  return (
    <ProfilePanel title="Compliance timeline" hint="Unified activity">
      <div className="flex flex-wrap gap-1.5 mb-4">
        {TIMELINE_FILTERS.map((chip) => {
          const count = counts[chip.id] ?? 0
          const active = filter === chip.id
          return (
            <button
              key={chip.id}
              type="button"
              onClick={() => setFilter(chip.id)}
              className={`px-3 py-1 rounded-full border text-xs font-semibold cursor-pointer ${
                active
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
              }`}
            >
              {chip.label} {count}
            </button>
          )
        })}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-slate-500 m-0">No compliance events in this category.</p>
      ) : (
        <>
          {visible.map((event) => (
            <div
              key={event.id}
              className="grid grid-cols-[100px_1fr_auto] gap-3 py-3 border-b border-slate-100 items-start max-sm:grid-cols-1"
            >
              <span
                className={`text-[11px] font-bold uppercase px-2 py-1 rounded-md text-center ${
                  CATEGORY_CLASS[event.category] || 'bg-slate-100 text-slate-600'
                }`}
              >
                {CATEGORY_LABEL[event.category] || event.label}
              </span>
              <div className="text-sm text-slate-800 min-w-0">
                {event.href ? (
                  <Link to={event.href} className="text-teal-700 hover:underline font-medium">
                    {event.title}
                  </Link>
                ) : event.amlCheckId ? (
                  <button
                    type="button"
                    onClick={() => onViewAmlCheck?.(event.amlCheckId)}
                    className="text-left text-teal-700 hover:underline font-medium bg-transparent border-0 cursor-pointer p-0 text-sm"
                  >
                    {event.title}
                  </button>
                ) : (
                  event.title
                )}
              </div>
              <span className="text-xs text-slate-500 whitespace-nowrap">{event.when}</span>
            </div>
          ))}
          {!expanded && hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="mt-3 text-teal-700 text-sm font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0"
            >
              Show {hiddenCount} more
            </button>
          )}
          {expanded && filtered.length > PREVIEW_LIMIT && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="mt-3 text-teal-700 text-sm font-semibold hover:underline bg-transparent border-0 cursor-pointer p-0"
            >
              Show less
            </button>
          )}
        </>
      )}
    </ProfilePanel>
  )
}
